import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import QuestionStep from "./steps/QuestionStep";
import ContextStep from "./steps/ContextStep";
import EvidenceStep from "./steps/EvidenceStep";
import ReviewStep from "./steps/ReviewStep";
import ResultView from "./ResultView";
import {
  emptyRainForm,
  RainFormData,
  WizardEvidenceItem,
} from "./types";
import {
  analyzeUnifiedEvidence,
  isValidYouTubeUrl,
} from "../../services/evidenceApi";
import {
  submitRainDecision,
  RainDecisionRequest,
  RainDecisionResponse,
  RainEvidencePayload,
} from "../../services/rainApi";
import "./RainWizard.css";

const STEPS = ["Question", "Context", "Evidence", "Review"];

type Phase = "form" | "submitting" | "result";

let evidenceIdCounter = 0;
const nextEvidenceId = () => `ev-${++evidenceIdCounter}`;

const DRAFT_KEY = "rain_wizard_draft_v1";
/** How long to wait after the last evidence add before auto-analyzing. */
const AUTO_ANALYZE_DELAY_MS = 2500;

interface StoredDraft {
  form: RainFormData;
  step: number;
  /** Only analyzed items (payload survives) and pending URLs are restorable;
      Files can't be persisted across a refresh. */
  evidence: WizardEvidenceItem[];
}

const loadDraft = (): StoredDraft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as StoredDraft;
    const hasContent =
      draft.form.question.trim() ||
      draft.form.options.some((o) => o.trim()) ||
      draft.evidence.length > 0;
    return hasContent ? draft : null;
  } catch {
    return null;
  }
};

/** Shape sent by the admin dashboard's Restore button via location.state. */
interface RestoreData {
  question?: string;
  options?: string[];
  context?: string;
  poolCreationDate?: string;
  aiDecision?: string;
  evidence?: any[];
}

/** Convert admin restore data into an initial wizard draft. Stored evidence
    was already analyzed, so items arrive ready to submit. */
const draftFromRestore = (restore: RestoreData): StoredDraft => ({
  form: {
    question: restore.question || "",
    options:
      restore.options && restore.options.length >= 2
        ? restore.options
        : ["", ""],
    context: restore.context || "",
    poolCreationDate: restore.poolCreationDate || "",
    aiDecision: restore.aiDecision || "",
  },
  step: 0,
  evidence: (restore.evidence || []).map((stored: any) => ({
    id: nextEvidenceId(),
    kind:
      stored.type === "youtube_video"
        ? "youtube"
        : stored.type === "API_data"
        ? "url"
        : "file",
    url: stored.youtubeUrl || (stored.type === "API_data" ? stored.fileName : undefined),
    status: stored.analysisSuccess ? "done" : "failed",
    error: stored.analysisSuccess ? undefined : "Analysis had failed in the original run",
    payload: {
      type: stored.type,
      description: stored.description || "",
      source: stored.source || "Disputer",
      timestamp: stored.timestamp || new Date().toISOString(),
      sha256_hash: stored.sha256_hash || "",
      fileName: stored.fileName || "",
      ...(stored.youtubeUrl ? { youtubeUrl: stored.youtubeUrl } : {}),
      aiAnalysis: stored.aiAnalysis || "",
      analysisConfidence: stored.analysisConfidence || 0,
      extractedText: stored.extractedText || "",
      analysisSuccess: !!stored.analysisSuccess,
      cloudinaryUrl: stored.cloudinaryUrl,
      cloudinaryPublicId: stored.cloudinaryPublicId,
      cloudinaryFormat: stored.cloudinaryFormat,
      fileSize: stored.fileSize,
      tokenCost: stored.tokenCost || null,
    },
  })),
});

const RainWizard: React.FC = () => {
  const location = useLocation();
  const restoreData = (location.state as any)?.restoreData as
    | RestoreData
    | undefined;
  // An explicit Restore from the admin dashboard wins over a saved draft.
  const [restoredDraft] = useState<StoredDraft | null>(() =>
    restoreData ? draftFromRestore(restoreData) : loadDraft()
  );
  const [isAdminRestore, setIsAdminRestore] = useState(!!restoreData);
  const [step, setStep] = useState(restoredDraft?.step ?? 0);
  const [phase, setPhase] = useState<Phase>("form");
  const [form, setForm] = useState<RainFormData>(
    restoredDraft?.form ?? emptyRainForm
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [evidence, setEvidence] = useState<WizardEvidenceItem[]>(
    restoredDraft?.evidence ?? []
  );
  const [showDraftBanner, setShowDraftBanner] = useState(
    !!restoredDraft && !restoreData
  );
  // Count of in-flight analysis batches - batches run concurrently, so a
  // boolean would flip to "idle" while another batch is still running.
  const [activeAnalyses, setActiveAnalyses] = useState(0);
  const isAnalyzing = activeAnalyses > 0;
  const [analyzeProgress, setAnalyzeProgress] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<RainDecisionResponse | null>(null);
  const [submittedOptions, setSubmittedOptions] = useState<string[]>([]);
  const [submittedQuestion, setSubmittedQuestion] = useState("");
  const [lastRequest, setLastRequest] = useState<RainDecisionRequest | null>(
    null
  );
  const analyzeRef = useRef<() => void>(() => {});

  /* Draft autosave - form fields, analyzed evidence and pending links survive
     a refresh. Raw File objects can't be persisted, so unanalyzed files are
     intentionally left out. */
  useEffect(() => {
    if (phase !== "form") return;
    try {
      const draft: StoredDraft = {
        form,
        step,
        evidence: evidence
          .filter(
            (item) =>
              item.status === "done" ||
              (item.kind !== "file" && item.status === "pending")
          )
          .map(({ file, ...rest }) => rest),
      };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch {
      /* storage full/unavailable - drafts are best-effort */
    }
  }, [form, evidence, step, phase]);

  /* Background analysis: once the user is on the evidence step, pending items
     start analyzing automatically after a short quiet period - so quick
     regrets (add then remove) cost nothing, and batches added together get
     analyzed together. Batches run CONCURRENTLY: an item added while a
     previous batch is in flight gets its own batch after the quiet period
     instead of waiting for the whole previous batch to finish. */
  useEffect(() => {
    if (phase !== "form" || step !== 2) return;
    if (!form.question.trim()) return;
    if (form.options.filter((o) => o.trim()).length < 2) return;
    if (!evidence.some((item) => item.status === "pending")) return;
    const timer = setTimeout(
      () => analyzeRef.current(),
      AUTO_ANALYZE_DELAY_MS
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence, step, phase]);

  const updateForm = (patch: Partial<RainFormData>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors({});
  };

  const validOptions = () => form.options.filter((o) => o.trim());

  const validateQuestionStep = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!form.question.trim()) {
      nextErrors.question = "Please write the question to decide.";
    }
    if (validOptions().length < 2) {
      nextErrors.options = "Please provide at least two outcomes.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /* ---------- Evidence handling ---------- */

  const addFiles = (files: File[]) => {
    const items: WizardEvidenceItem[] = files.map((file) => ({
      id: nextEvidenceId(),
      kind: "file",
      file,
      status: "pending",
    }));
    setEvidence((prev) => [...prev, ...items]);
  };

  const addUrl = (url: string): string | null => {
    if (evidence.some((item) => item.url === url)) {
      return "This link was already added.";
    }
    setEvidence((prev) => [
      ...prev,
      {
        id: nextEvidenceId(),
        kind: isValidYouTubeUrl(url) ? "youtube" : "url",
        url,
        status: "pending",
      },
    ]);
    return null;
  };

  const removeEvidence = (id: string) => {
    setEvidence((prev) => prev.filter((item) => item.id !== id));
  };

  const buildPayloadFromAnalysis = (
    item: WizardEvidenceItem,
    analysisResult: any
  ): RainEvidencePayload => {
    const info = analysisResult?.fileInfo || {};
    const analysis = analysisResult?.analysis || {};
    const evidenceData = analysisResult?.evidenceData || {};
    const isFile = item.kind === "file";
    const mimeType: string = info.mimeType || item.file?.type || "";
    const type = isFile
      ? mimeType.startsWith("image/")
        ? "photo"
        : mimeType.startsWith("video/")
        ? "video"
        : mimeType === "application/pdf"
        ? "pdf"
        : "text_note"
      : item.kind === "youtube"
      ? "youtube_video"
      : "API_data";

    return {
      type,
      description: isFile
        ? `Uploaded file: ${info.originalName || item.file?.name || "file"}`
        : item.kind === "youtube"
        ? `YouTube: ${item.url}`
        : `Web URL: ${item.url}`,
      source: "Disputer",
      timestamp: new Date().toISOString(),
      sha256_hash: info.sha256Hash || "",
      fileName: isFile
        ? info.originalName || item.file?.name || ""
        : item.url || "",
      ...(item.kind === "youtube" ? { youtubeUrl: item.url } : {}),
      aiAnalysis: analysis.analysis || "",
      analysisConfidence: analysis.confidence || 0,
      extractedText: analysis.extractedText || "",
      analysisSuccess: !!analysisResult?.success,
      cloudinaryUrl: evidenceData.cloudinaryUrl,
      cloudinaryPublicId: evidenceData.cloudinaryPublicId,
      cloudinaryFormat: evidenceData.cloudinaryFormat,
      fileSize: info.size,
      tokenCost: analysis.tokenCost || null,
    };
  };

  const analyzeEvidence = async () => {
    const pending = evidence.filter((item) => item.status === "pending");
    if (pending.length === 0) return;

    // Batches may run concurrently - every state update below is scoped to
    // this batch's item ids so a failure here never clobbers another batch.
    const batchIds = new Set(pending.map((item) => item.id));

    setActiveAnalyses((count) => count + 1);
    setEvidence((prev) =>
      prev.map((item) =>
        batchIds.has(item.id) ? { ...item, status: "analyzing" } : item
      )
    );

    const files = pending.filter((i) => i.kind === "file");
    const youtube = pending.filter((i) => i.kind === "youtube");
    const urls = pending.filter((i) => i.kind === "url");

    try {
      const response = await analyzeUnifiedEvidence(
        {
          files: files.map((i) => i.file!),
          youtubeUrls: youtube.map((i) => i.url!),
          urls: urls.map((i) => i.url!),
          source: "Disputer",
          question: form.question.trim() || undefined,
          options: validOptions().length >= 2 ? validOptions() : undefined,
        },
        (progress) => setAnalyzeProgress(progress < 100 ? progress : null)
      );

      // Results come back grouped and ordered the same way they were sent.
      const updates = new Map<string, Partial<WizardEvidenceItem>>();
      const applyGroup = (
        sent: WizardEvidenceItem[],
        results: any[] | undefined
      ) => {
        sent.forEach((item, index) => {
          const res = results?.[index];
          if (res?.success) {
            updates.set(item.id, {
              status: "done",
              payload: buildPayloadFromAnalysis(item, res),
            });
          } else {
            updates.set(item.id, {
              status: "failed",
              error: res?.error || "The analysis did not complete.",
            });
          }
        });
      };
      applyGroup(files, response.results?.files);
      applyGroup(youtube, response.results?.youtubeUrls);
      applyGroup(urls, response.results?.urls);

      setEvidence((prev) =>
        prev.map((item) =>
          updates.has(item.id) ? { ...item, ...updates.get(item.id) } : item
        )
      );
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Evidence analysis failed.";
      setEvidence((prev) =>
        prev.map((item) =>
          batchIds.has(item.id) && item.status === "analyzing"
            ? { ...item, status: "failed", error: message }
            : item
        )
      );
    } finally {
      setActiveAnalyses((count) => count - 1);
      setAnalyzeProgress(null);
    }
  };
  analyzeRef.current = analyzeEvidence;

  /* ---------- Navigation & submit ---------- */

  const goNext = async () => {
    if (step === 0 && !validateQuestionStep()) return;
    if (step === 2) {
      // Analyze anything still pending before the review step.
      if (evidence.some((item) => item.status === "pending")) {
        await analyzeEvidence();
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const buildRequestPayload = (): RainDecisionRequest => ({
    question: form.question.trim(),
    options: validOptions(),
    statement_of_claim: form.context.trim() || undefined,
    pool_creation_date: form.poolCreationDate || undefined,
    ai_decision: form.aiDecision.trim() || undefined,
    submitted_evidence: evidence
      .filter((item) => item.status === "done" && item.payload)
      .map((item) => item.payload!),
  });

  const submit = async () => {
    setPhase("submitting");
    setSubmitError(null);
    const payload = buildRequestPayload();
    setSubmittedOptions(payload.options);
    setSubmittedQuestion(payload.question);
    setLastRequest(payload);

    try {
      const response = await submitRainDecision(payload);
      setResult(response);
      setPhase("result");
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {}
    } catch (error: any) {
      setSubmitError(
        error?.response?.data?.error ||
          error?.message ||
          "The request failed. Please try again."
      );
      setPhase("form");
    }
  };

  const reset = () => {
    setForm(emptyRainForm);
    setEvidence([]);
    setResult(null);
    setErrors({});
    setSubmitError(null);
    setStep(0);
    setPhase("form");
    setShowDraftBanner(false);
    setIsAdminRestore(false);
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
  };

  /* ---------- Render ---------- */

  if (phase === "submitting") {
    return (
      <div className="rw-container">
        <div className="rw-card rw-waiting">
          <div className="rw-spinner" />
          <h2 className="rw-step-title">The judge is working</h2>
          <p className="rw-step-subtitle">
            Researching sources, weighing evidence and reaching a decision.
            This usually takes under a minute - please keep this page open.
          </p>
        </div>
      </div>
    );
  }

  if (phase === "result" && result) {
    return (
      <div className="rw-container">
        <div className="rw-card">
          <ResultView
            result={result}
            options={submittedOptions}
            question={submittedQuestion}
            rawRequest={lastRequest}
            onNewRequest={reset}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rw-container">
      {isAdminRestore && (
        <div className="rw-draft-banner">
          <span>
            Restored a past decision for re-testing - review and submit to run
            it again with the current state of the world.
          </span>
          <span className="rw-draft-actions">
            <button type="button" className="rw-copy-btn" onClick={reset}>
              Start fresh
            </button>
          </span>
        </div>
      )}
      {showDraftBanner && (
        <div className="rw-draft-banner">
          <span>
            Restored your unfinished draft. Files that weren't analyzed yet
            need to be re-added. If you submitted before refreshing, check
            History.
          </span>
          <span className="rw-draft-actions">
            <button
              type="button"
              className="rw-copy-btn"
              onClick={() => setShowDraftBanner(false)}
            >
              Keep draft
            </button>
            <button type="button" className="rw-copy-btn" onClick={reset}>
              Start fresh
            </button>
          </span>
        </div>
      )}
      <div className="rw-card">
        <div className="rw-stepper">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`rw-stepper-item ${i === step ? "rw-current" : ""} ${
                i < step ? "rw-done" : ""
              }`}
              onClick={() => i < step && setStep(i)}
            >
              <span className="rw-stepper-dot">{i + 1}</span>
              <span className="rw-stepper-label">{label}</span>
            </div>
          ))}
        </div>

        {step === 0 && (
          <QuestionStep form={form} errors={errors} onChange={updateForm} />
        )}
        {step === 1 && <ContextStep form={form} onChange={updateForm} />}
        {step === 2 && (
          <EvidenceStep
            items={evidence}
            isAnalyzing={isAnalyzing}
            analyzeProgress={analyzeProgress}
            onAddFiles={addFiles}
            onAddUrl={addUrl}
            onRemove={removeEvidence}
            onAnalyze={analyzeEvidence}
          />
        )}
        {step === 3 && (
          <ReviewStep
            form={form}
            evidence={evidence}
            payload={buildRequestPayload()}
          />
        )}

        {submitError && <div className="rw-error rw-submit-error">{submitError}</div>}

        <div className="rw-actions">
          {step > 0 && (
            <button type="button" className="rw-secondary-btn" onClick={goBack}>
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="rw-primary-btn"
              disabled={isAnalyzing}
              onClick={goNext}
            >
              {step === 2 && evidence.some((i) => i.status === "pending")
                ? "Analyze & continue"
                : "Continue"}
            </button>
          ) : (
            <button
              type="button"
              className="rw-primary-btn"
              disabled={isAnalyzing}
              onClick={submit}
            >
              Submit for decision
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RainWizard;
